import React from 'react';
import { Box, Text, UnstyledButton } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AttrRow, AttrValue, NO, ProductInfo, TooltipKey, YES } from '../compareData';
import { CheckIcon, CrossIcon } from '../CompareIcons';
import { GRAY, INK } from '../constants';
import { ColumnBlock } from './ColumnBlock';
import styles from './AttributeCell.module.scss';

/** One attribute value: Yes/No checkmark or a spec line. */
const AttributeValue: React.FC<{ value: AttrValue }> = ({ value }) => {
  const { t } = useTranslation();
  if (value === YES || value === NO) {
    const yes = value === YES;
    return (
      <Box fw={500} c={yes ? INK : GRAY} className={styles.value}>
        <Box bg={yes ? '#c9f0d5' : '#e5e5e2'} className={styles.valueIcon}>
          {yes ? <CheckIcon /> : <CrossIcon />}
        </Box>
        {yes ? t('compare_v2_yes') : t('compare_v2_no')}
      </Box>
    );
  }
  return (
    <Box fw={500} className={styles.spec}>
      {t(value.valueKey)}
      {value.subKey && (
        <Text component="span" c={GRAY} fz={14} fw={400} mt={3} display="block">
          {t(value.subKey)}
        </Text>
      )}
    </Box>
  );
};

interface AttributeCellProps {
  row: AttrRow;
  product: ProductInfo;
  onOpenTooltip: (key: TooltipKey) => void;
}

/** An attribute cell: repeated label (+ optional info trigger) and its value. */
export const AttributeCell: React.FC<AttributeCellProps> = ({ row, product, onOpenTooltip }) => {
  const { t } = useTranslation();
  const value = product.attrs[row.attrKey];
  const label = t(row.labelKey);
  // Info icon only on columns that have the feature.
  const showInfo = Boolean(row.tooltip) && value === YES;

  let title: React.ReactNode = label;
  if (showInfo && row.tooltip) {
    const tooltip = row.tooltip;
    // Glue the icon to the last word so it never orphans.
    const words = label.split(' ');
    const last = words.pop();
    const lead = words.join(' ');
    title = (
      <>
        {lead ? `${lead} ` : null}
        {/* Inherit the label's 14px like the v6 default did; v7 Text otherwise resets to the theme size. */}
        <Text component="span" inherit lh={1.55} className={styles.noWrap}>
          {last}
          <UnstyledButton
            type="button"
            w={17}
            h={17}
            p={0}
            ml={5}
            aria-label={t('compare_v2_more_info')}
            onClick={() => onOpenTooltip(tooltip)}
            className={styles.infoButton}
          />
        </Text>
      </>
    );
  }

  return (
    <ColumnBlock title={title}>
      <AttributeValue value={value} />
    </ColumnBlock>
  );
};
